import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { IconArmchair as Armchair, IconBuildingSkyscraper as Building2, IconCheck as Check, IconChevronRight as ChevronRight, IconWorld as Globe, IconLoader2 as Loader2, IconLock as Lock, IconMapPin as MapPin, IconPlus as Plus, IconSearch as Search, IconX as X } from '@tabler/icons-react';
import type { Venue, VenueListScope } from '@/types/venue';
import { fetchVenue, fetchVenues } from '@/api/venueApi';
import VenueFormModal from '@/components/VenueFormModal';

export type VenuePickerValue = { name: string; venueId: string | null; venue: Venue | null };

type Props = {
    name: string;
    venueId?: string | null;
    onChange: (next: VenuePickerValue) => void;
    placeholder?: string;
    invalid?: boolean;
    allVenues?: boolean;
    organizerId?: string;
};

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

export const countSeats = (seatMap: any): number =>
    (seatMap?.zones ?? []).reduce((n: number, z: any) => n + (z.rows ?? []).reduce((m: number, r: any) => m + (r.seats?.length ?? 0), 0), 0);

const useDebounced = <T,>(value: T, ms: number) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return v;
};

const VenueBadges: React.FC<{ venue: Venue }> = ({ venue }) => {
    const seats = countSeats(venue.seatMap);
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {seats > 0 ? (
                <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-primary99 text-primary30 dark:bg-nightAccent/15 dark:text-nightAccent text-[10px] font-semibold">
                    <Armchair className="w-3 h-3" /> Seat map · {seats} seats
                </span>
            ) : (
                <span className="inline-flex items-center h-5 px-2 rounded-full bg-grey99 text-grey40 dark:bg-white/[0.06] dark:text-nightMuted text-[10px] font-medium">General admission</span>
            )}
            {venue.visibility === 'selected' ? (
                <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-grey99 text-grey40 dark:bg-white/[0.06] dark:text-nightMuted text-[10px] font-medium"><Lock className="w-3 h-3" /> Selected organizers</span>
            ) : venue.canEdit ? (
                <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-grey99 text-grey40 dark:bg-white/[0.06] dark:text-nightMuted text-[10px] font-medium"><Globe className="w-3 h-3" /> All organizers</span>
            ) : null}
            {venue.canEdit && <span className="inline-flex items-center h-5 px-2 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300 text-[10px] font-medium">Yours</span>}
        </div>
    );
};

const VenueTile: React.FC<{ venue: Venue; size?: 'sm' | 'md' }> = ({ venue, size = 'md' }) => (
    <span className={cx(
        'flex-shrink-0 rounded-2xl flex items-center justify-center',
        size === 'sm' ? 'w-9 h-9' : 'w-12 h-12',
        venue.seatMap ? 'bg-primary99 text-primary40 dark:bg-nightAccent/10 dark:text-nightAccent' : 'bg-grey99 text-grey50 dark:bg-white/[0.06] dark:text-nightMuted',
    )}>
        {venue.seatMap ? <Armchair className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} /> : <Building2 className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />}
    </span>
);

const VenueRow: React.FC<{ venue: Venue; active?: boolean; selected?: boolean; onPick: () => void; id?: string }> = ({ venue, active, selected, onPick, id }) => (
    <button
        id={id}
        type="button"
        role="option"
        aria-selected={selected}
        onMouseDown={e => e.preventDefault()}
        onClick={onPick}
        className={cx(
            'w-full flex items-start gap-3 p-3 text-left rounded-2xl transition-colors',
            active ? 'bg-grey99 dark:bg-white/10' : 'hover:bg-grey99 dark:hover:bg-white/5',
        )}
    >
        <VenueTile venue={venue} size="sm" />
        <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold text-grey10 dark:text-white truncate">{venue.name}</span>
            {venue.address?.fullAddress && <span className="block text-xs text-grey40 dark:text-nightMuted truncate mb-1">{venue.address.fullAddress}</span>}
            <VenueBadges venue={venue} />
        </span>
        {selected && <Check className="w-4 h-4 mt-1 text-primary40 dark:text-nightAccent flex-shrink-0" />}
    </button>
);

const VenueBrowser: React.FC<{
    open: boolean;
    selectedId: string | null;
    onClose: () => void;
    onPick: (venue: Venue) => void;
    onCreate: () => void;
    allVenues?: boolean;
    organizerId?: string;
}> = ({ open, selectedId, onClose, onPick, onCreate, allVenues, organizerId }) => {
    const [search, setSearch] = useState('');
    const [scope, setScope] = useState<VenueListScope>(allVenues ? 'manage' : 'available');
    const [seatMapOnly, setSeatMapOnly] = useState(false);
    const debounced = useDebounced(search.trim(), 250);

    const query = useInfiniteQuery({
        queryKey: ['venue-browser', debounced, scope, organizerId],
        queryFn: ({ pageParam }) => fetchVenues({ page: pageParam, limit: 20, search: debounced || undefined, scope, organizerId: scope === 'mine' ? organizerId : undefined }),
        initialPageParam: 1,
        getNextPageParam: last => (last.pagination && last.pagination.page < last.pagination.totalPages ? last.pagination.page + 1 : undefined),
        enabled: open,
    });

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    const venues = useMemo(() => {
        const all = (query.data?.pages ?? []).flatMap(p => p.data ?? []);
        return seatMapOnly ? all.filter(v => countSeats(v.seatMap) > 0) : all;
    }, [query.data, seatMapOnly]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[9000] flex items-stretch sm:items-center justify-center bg-black/50 sm:p-6" onMouseDown={e => e.target === e.currentTarget && onClose()}>
            <div role="dialog" aria-modal="true" aria-label="Choose a venue" className="flex flex-col w-full sm:max-w-2xl sm:max-h-[85vh] bg-white dark:bg-nightSurface sm:rounded-[28px] sm:ring-1 sm:ring-black/[0.06] sm:dark:ring-white/10 shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 pt-5 pb-4">
                    <div>
                        <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Choose a venue</h2>
                        <p className="mt-1 text-sm text-grey40 dark:text-nightMuted">Venues with a seat map let buyers pick their seats.</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full text-grey20 ring-1 ring-grey90 hover:bg-grey99 dark:text-nightMuted dark:ring-white/15 dark:hover:bg-white/10">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-4 sm:px-6 pb-4 space-y-3 border-b border-black/[0.06] dark:border-white/10">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-grey60 dark:text-nightMuted" />
                        <input
                            autoFocus
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search by venue name"
                            className="w-full h-12 pl-11 pr-4 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/10 text-base text-grey10 dark:text-white placeholder:text-grey60 dark:placeholder:text-nightMuted outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-nightAccent/60"
                        />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-1 p-1 rounded-full bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]" role="tablist">
                            {([[allVenues ? 'manage' : 'available', 'All venues'], ['mine', 'My venues']] as const).map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    role="tab"
                                    aria-selected={scope === value}
                                    onClick={() => setScope(value)}
                                    className={cx('px-4 h-8 rounded-full text-sm font-semibold whitespace-nowrap transition-all', scope === value ? 'bg-grey10 text-white shadow-sm dark:bg-white dark:text-nightBg' : 'text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white')}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <label className="flex items-center gap-2 text-sm font-medium text-grey20 dark:text-nightTextSecondary cursor-pointer select-none">
                            <input type="checkbox" checked={seatMapOnly} onChange={e => setSeatMapOnly(e.target.checked)} className="accent-primary40 dark:accent-nightAccent w-4 h-4" />
                            Has seat map
                        </label>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-2 sm:px-3 py-2" role="listbox">
                    {query.isLoading ? (
                        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary40 dark:text-nightAccent" /></div>
                    ) : venues.length ? (
                        <>
                            {venues.map(v => (
                                <VenueRow key={v._id} venue={v} selected={v._id === selectedId} onPick={() => onPick(v)} />
                            ))}
                            {query.hasNextPage && (
                                <button
                                    type="button"
                                    onClick={() => query.fetchNextPage()}
                                    disabled={query.isFetchingNextPage}
                                    className="w-full h-10 mt-1 rounded-full text-sm font-semibold text-grey10 ring-1 ring-grey90 dark:text-white dark:ring-white/15 hover:bg-grey99 dark:hover:bg-white/[0.06] disabled:opacity-60"
                                >
                                    {query.isFetchingNextPage ? 'Loading...' : 'Load more'}
                                </button>
                            )}
                        </>
                    ) : (
                        <div className="py-14 text-center">
                            <MapPin className="w-8 h-8 mx-auto mb-3 text-grey70 dark:text-white/30" />
                            <div className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">No venues found</div>
                            <div className="mt-1 text-sm text-grey40 dark:text-nightMuted">Try another search or create a new venue.</div>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-black/[0.06] dark:border-white/10" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
                    <button type="button" onClick={onCreate} className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-primary text-sm font-semibold text-white hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70">
                        <Plus className="w-4 h-4" /> New venue
                    </button>
                    <button type="button" onClick={onClose} className="h-11 px-5 rounded-full text-sm font-semibold text-grey10 ring-1 ring-grey90 dark:text-white dark:ring-white/15 hover:bg-grey99 dark:hover:bg-white/[0.06]">Cancel</button>
                </div>
            </div>
        </div>
    );
};

const VenuePicker: React.FC<Props> = ({ name, venueId, onChange, placeholder = 'Search saved venues or type a venue name', invalid, allVenues, organizerId }) => {
    const listId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const [focused, setFocused] = useState(false);
    const [active, setActive] = useState(-1);
    const [browsing, setBrowsing] = useState(false);
    const [creating, setCreating] = useState(false);
    const debounced = useDebounced(name.trim(), 250);

    const { data: venueRes, isLoading: venueLoading } = useQuery({
        queryKey: ['venue', venueId],
        queryFn: () => fetchVenue(venueId!),
        enabled: Boolean(venueId),
    });
    const selected = venueId ? venueRes?.data ?? null : null;

    const { data: results, isFetching } = useQuery({
        queryKey: ['venue-picker', debounced, allVenues, organizerId],
        queryFn: () => (debounced ? fetchVenues({ search: debounced, limit: 6, scope: allVenues ? 'manage' : undefined }) : fetchVenues({ limit: 5, scope: 'mine', organizerId })),
        enabled: focused && !venueId,
    });
    const options = results?.data ?? [];

    useEffect(() => setActive(-1), [debounced]);

    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) setFocused(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    const pick = (venue: Venue) => {
        onChange({ name: venue.name, venueId: venue._id, venue });
        setFocused(false);
        setBrowsing(false);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!focused) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive(i => Math.min(options.length - 1, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(i => Math.max(-1, i - 1));
        } else if (e.key === 'Enter' && active >= 0 && options[active]) {
            e.preventDefault();
            pick(options[active]);
        } else if (e.key === 'Escape') {
            setFocused(false);
        }
    };

    const modals = (
        <>
            <VenueBrowser
                open={browsing}
                selectedId={venueId ?? null}
                onClose={() => setBrowsing(false)}
                onPick={pick}
                onCreate={() => setCreating(true)}
                allVenues={allVenues}
                organizerId={organizerId}
            />
            {creating && (
                <VenueFormModal
                    isOpen={creating}
                    venue={null}
                    onClose={() => setCreating(false)}
                    onSaved={v => pick(v)}
                />
            )}
        </>
    );

    if (venueId) {
        return (
            <div className="rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/10 p-3 sm:p-4">
                {venueLoading && !selected ? (
                    <div className="flex items-center gap-2 text-sm text-grey40 dark:text-nightMuted"><Loader2 className="w-4 h-4 animate-spin" /> Loading venue...</div>
                ) : (
                    <div className="flex items-start gap-3">
                        {selected ? <VenueTile venue={selected} /> : <span className="w-12 h-12 rounded-2xl bg-white dark:bg-white/[0.06] flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                            <div className="text-base font-bold tracking-[-0.01em] text-grey10 dark:text-white truncate">{selected?.name || name}</div>
                            {selected?.address?.fullAddress && (
                                <div className="flex items-center gap-1 text-xs text-grey40 dark:text-nightMuted mb-1.5 min-w-0">
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{selected.address.fullAddress}</span>
                                </div>
                            )}
                            {selected && <VenueBadges venue={selected} />}
                        </div>
                        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 flex-shrink-0">
                            <button type="button" onClick={() => setBrowsing(true)} className="h-9 px-4 rounded-full bg-white text-xs font-semibold text-grey10 ring-1 ring-grey90 hover:bg-grey95 dark:bg-transparent dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]">Change</button>
                            <button
                                type="button"
                                onClick={() => onChange({ name: '', venueId: null, venue: null })}
                                aria-label="Remove venue"
                                className="h-9 w-9 flex items-center justify-center rounded-full text-grey40 hover:bg-white hover:text-error40 dark:text-nightMuted dark:hover:bg-white/[0.06]"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
                {modals}
            </div>
        );
    }

    const showDropdown = focused;

    return (
        <div ref={containerRef} className="relative">
            <div className={cx(
                'flex items-center gap-2 h-12 pl-4 pr-1.5 rounded-2xl bg-grey99 dark:bg-white/[0.06] transition-shadow',
                invalid ? 'ring-2 ring-error50' : focused ? 'ring-2 ring-primary/50 dark:ring-nightAccent/60' : 'ring-1 ring-black/[0.04] dark:ring-white/10',
            )}>
                <Search className="w-4 h-4 text-grey60 dark:text-nightMuted flex-shrink-0" />
                <input
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-controls={listId}
                    aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
                    value={name}
                    placeholder={placeholder}
                    onChange={e => onChange({ name: e.target.value, venueId: null, venue: null })}
                    onFocus={() => setFocused(true)}
                    onKeyDown={onKeyDown}
                    className="flex-1 min-w-0 bg-transparent text-base text-grey10 dark:text-white outline-none placeholder:text-grey60 dark:placeholder:text-nightMuted"
                />
                {isFetching && focused && <Loader2 className="w-4 h-4 animate-spin text-grey60 dark:text-nightMuted" />}
                <button
                    type="button"
                    onClick={() => setBrowsing(true)}
                    className="hidden sm:inline-flex items-center gap-1 h-9 px-3.5 rounded-full bg-white text-xs font-semibold text-grey10 ring-1 ring-black/[0.06] hover:bg-grey95 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 flex-shrink-0"
                >
                    Browse <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>
            <button type="button" onClick={() => setBrowsing(true)} className="sm:hidden mt-2 inline-flex h-9 items-center rounded-full px-4 text-xs font-semibold text-grey10 ring-1 ring-grey90 dark:text-white dark:ring-white/15">
                Browse saved venues
            </button>

            {showDropdown && (
                <div className="absolute z-30 left-0 right-0 mt-2 rounded-[20px] ring-1 ring-black/[0.06] dark:ring-white/10 bg-white dark:bg-nightElevated shadow-xl overflow-hidden">
                    <div className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-grey40 dark:text-nightMuted">
                        {debounced ? 'Saved venues' : 'Your venues'}
                    </div>
                    <div id={listId} role="listbox" className="max-h-72 overflow-y-auto px-1.5 pb-1.5">
                        {options.length ? (
                            options.map((v, i) => (
                                <VenueRow key={v._id} id={`${listId}-${i}`} venue={v} active={i === active} onPick={() => pick(v)} />
                            ))
                        ) : !isFetching ? (
                            <div className="px-3 py-3 text-xs text-grey40 dark:text-nightMuted">
                                {debounced ? 'No saved venues match. The name you typed will be used.' : 'You have no saved venues yet.'}
                            </div>
                        ) : null}
                    </div>
                    <div className="flex items-center gap-1.5 border-t border-black/[0.06] dark:border-white/10 p-2">
                        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setBrowsing(true)} className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full text-xs font-semibold text-grey10 ring-1 ring-grey90 dark:text-white dark:ring-white/15 hover:bg-grey99 dark:hover:bg-white/[0.06]">
                            <Search className="w-3.5 h-3.5" /> Browse all
                        </button>
                        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setCreating(true)} className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-primary text-xs font-semibold text-white hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70">
                            <Plus className="w-3.5 h-3.5" /> New venue
                        </button>
                    </div>
                </div>
            )}
            {modals}
        </div>
    );
};

export default VenuePicker;
